import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Menu, X, Car, User, LogIn, Shield, Key, Clock, Users, Bell, Calendar, Calculator, Lightbulb, Trophy, Gift, MessageCircle, Camera, Swords, Warehouse, AlertTriangle, ShoppingBag, Ticket, ChevronDown, Heart, Target, Spade, Wrench, Megaphone } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

// Default item ids whose lucide-react icons are imported above (used as fallback)
// All other icons resolve dynamically from `lucide-react` exports via Icons[name].
const resolveIcon = (name) => {
  if (!name) return null;
  const C = Icons[name];
  return C || null;
};

const COLOR_CLASS = {
  blue: "text-blue-400 hover:text-blue-300 hover:bg-blue-900/20",
  pink: "text-pink-400 hover:text-pink-300 hover:bg-pink-900/20",
  green: "text-green-400 hover:text-green-300 hover:bg-green-900/20",
  red: "text-red-400 hover:text-red-300 hover:bg-red-900/20",
  yellow: "text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20",
  purple: "text-purple-400 hover:text-purple-300 hover:bg-purple-900/20",
  orange: "text-orange-400 hover:text-orange-300 hover:bg-orange-900/20",
};

const NavLink = ({ to, active, children, className = "" }) => (
  <Link to={to}>
    <Button
      variant={active ? "secondary" : "ghost"}
      size="sm"
      className={active
        ? "bg-orange-500 hover:bg-orange-600 text-white h-8 px-2.5 text-xs"
        : `text-gray-300 hover:text-white hover:bg-white/10 h-8 px-2.5 text-xs ${className}`}
    >
      {children}
    </Button>
  </Link>
);

// Hover Dropdown Component
const NavDropdown = ({ label, icon: Icon, items, location }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasActiveItem = items.some(item => location.pathname === item.to);
  
  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Button
        variant="ghost"
        size="sm"
        className={`text-gray-300 hover:text-white hover:bg-white/10 h-8 px-2.5 text-xs flex items-center gap-1 ${hasActiveItem ? 'bg-orange-500/20 text-orange-400' : ''}`}
      >
        {Icon && <Icon className="h-3 w-3" />}
        {label}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </Button>
      
      {isOpen && (
        <div className="absolute top-full left-0 pt-1 z-50">
          <div className="bg-black/95 backdrop-blur-xl border border-orange-500/20 rounded-lg shadow-xl shadow-black/50 py-1 min-w-[180px]">
            {items.map((item, index) => (
              <Link
                key={index}
                to={item.to}
                className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  location.pathname === item.to
                    ? 'bg-orange-500/20 text-orange-400'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                {item.icon && <item.icon className="h-4 w-4" />}
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, login, logout, isAuthenticated, isFullyAuthenticated, isAdmin, isLandlord, isTenant, systemSettings } = useAuth();
  const [navConfig, setNavConfig] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${API}/api/nav-config/`).then(r => setNavConfig(r.data?.items || null)).catch(() => setNavConfig(null));
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const isActive = (path) => location.pathname === path;

  // Role-based visibility
  const role = (user?.role || '').toLowerCase();
  const isPromoter = role === 'promoter';
  const canSee = (roles) => {
    if (!roles || roles.includes('any')) return true;
    if (roles.includes('auth') && isFullyAuthenticated) return true;
    if (roles.includes('admin') && isAdmin) return true;
    if (roles.includes('landlord') && isLandlord) return true;
    if (roles.includes('promoter') && (isPromoter || isAdmin)) return true;
    return false;
  };

  // Filter tree: drop invisible nodes, drop empty groups
  const visibleItems = useMemo(() => {
    if (!navConfig) return [];
    const filter = (nodes) => nodes
      .filter(n => n.visible !== false && canSee(n.roles))
      .map(n => {
        if (n.type === 'group') {
          const kids = filter(n.children || []);
          return kids.length ? { ...n, children: kids } : null;
        }
        return n;
      })
      .filter(Boolean);
    return filter(navConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navConfig, isFullyAuthenticated, isAdmin, isLandlord, isPromoter]);

  return (
    <nav className="fixed w-full z-50 bg-black/60 backdrop-blur-md border-b border-orange-500/20 shadow-lg shadow-orange-500/5">
      <div className="w-full px-3 xl:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-2 group" data-testid="nav-logo">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 to-blue-600 rounded-lg blur opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
              <div className="relative w-10 h-10 bg-black rounded-lg flex items-center justify-center border border-white/10 overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_fleet-rental-10/artifacts/u7jw0pff_ChatGPT%20Image%202026.%20m%C3%A1rc.%203.%2000_07_31.png" alt="GRS" className="w-full h-full object-cover" />
              </div>
            </div>
            <span className="font-bold text-base tracking-wider text-white hidden lg:inline">Gonzalez Road Services</span>
            <span className="font-bold text-base tracking-wider text-white lg:hidden">GRS</span>
          </Link>

          {/* Desktop Menu - Config-driven, admin editable */}
          <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {visibleItems.map((node) => {
              const Icon = resolveIcon(node.icon);
              const colorClass = COLOR_CLASS[node.color] || '';
              if (node.type === 'group') {
                return (
                  <NavDropdown
                    key={node.id}
                    label={node.label}
                    icon={Icon || ChevronDown}
                    location={location}
                    items={(node.children || []).map(c => ({
                      to: c.path,
                      label: c.label,
                      icon: resolveIcon(c.icon) || ChevronDown,
                    }))}
                  />
                );
              }
              // Link
              return (
                <NavLink
                  key={node.id}
                  to={node.path}
                  active={isActive(node.path)}
                  className={colorClass}
                >
                  {Icon ? <Icon className="mr-1 h-3 w-3" /> : null}
                  {node.label}
                </NavLink>
              );
            })}
          </div>

          {/* Right side - Discord, notifications, profile */}
          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
            <a href={systemSettings?.discord_invite_url || "https://discord.gg/S2CH8JSAP3"} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300 hover:border-blue-400 h-8 px-2.5 text-xs" data-testid="nav-discord-btn">Discord</Button>
            </a>

            {isFullyAuthenticated && <NotificationBell />}

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full ring-2 ring-orange-500/20 hover:ring-orange-500/50 transition-all p-0" data-testid="nav-user-menu">
                    <Avatar className="h-8 w-8 border border-black/50">
                      <AvatarImage src={user?.avatar} alt={user?.username} />
                      <AvatarFallback className="bg-orange-600 text-white text-xs">{user?.username?.charAt(0)}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-black/95 border-orange-500/20 text-white" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none text-orange-400">{user?.username}</p>
                      <p className="text-xs leading-none text-gray-400">{user?.ic_name || "Nincs IC név"}</p>
                      <Badge className="mt-1 w-fit bg-blue-600 hover:bg-blue-700 border-none">{user?.role}</Badge>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem asChild className="focus:bg-white/10 focus:text-white cursor-pointer">
                    <Link to="/profile"><User className="mr-2 h-4 w-4" /><span>Profil</span></Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={logout} className="text-red-400 focus:text-red-300 focus:bg-red-900/20 cursor-pointer">
                    <LogIn className="mr-2 h-4 w-4 rotate-180" /><span>Kijelentkezés</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={login} size="sm" className="bg-[#5865F2] hover:bg-[#4752C4] text-white shadow-lg shadow-blue-500/20 h-8 px-3 text-xs" data-testid="nav-login-btn">
                Bejelentkezés
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {isAuthenticated && (
              <Link to="/profile">
                <Avatar className="h-8 w-8 border border-orange-500/50">
                  <AvatarImage src={user?.avatar} alt={user?.username} />
                  <AvatarFallback className="bg-orange-600">{user?.username?.charAt(0)}</AvatarFallback>
                </Avatar>
              </Link>
            )}
            <Button variant="ghost" size="icon" onClick={toggleMenu} className="text-white hover:bg-white/10">
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-b border-orange-500/20 max-h-[80vh] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link to="/" onClick={() => setIsOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10">Főoldal</Button>
            </Link>
            <Link to="/rules" onClick={() => setIsOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10">
                <Shield className="mr-2 h-4 w-4" /> Szabályzat
              </Button>
            </Link>
            
            {isFullyAuthenticated && (
              <>
                {/* Autók Category */}
                <div className="pt-2 pb-1">
                  <span className="px-3 text-xs font-semibold text-orange-500 uppercase tracking-wider">🚗 Autók</span>
                </div>
                <Link to="/fleet" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Car className="mr-2 h-4 w-4" /> Flotta
                  </Button>
                </Link>
                <Link to="/favorites" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Heart className="mr-2 h-4 w-4" /> Kedvenceim
                  </Button>
                </Link>
                <Link to="/garage" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Warehouse className="mr-2 h-4 w-4" /> Garázs
                  </Button>
                </Link>
                <Link to="/configurator" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Calculator className="mr-2 h-4 w-4" /> Kalkulátor
                  </Button>
                </Link>
                <Link to="/seemta-v4" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Wrench className="mr-2 h-4 w-4" /> Megvásárolható járművek
                  </Button>
                </Link>

                {/* Közösség Category */}
                <div className="pt-2 pb-1">
                  <span className="px-3 text-xs font-semibold text-orange-500 uppercase tracking-wider">👥 Közösség</span>
                </div>
                <Link to="/gallery" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Camera className="mr-2 h-4 w-4" /> Galéria
                  </Button>
                </Link>
                <Link to="/challenges" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Swords className="mr-2 h-4 w-4" /> Kihívások
                  </Button>
                </Link>
                <Link to="/suggestions" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Lightbulb className="mr-2 h-4 w-4" /> Javaslatok
                  </Button>
                </Link>
                <Link to="/events" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Trophy className="mr-2 h-4 w-4" /> Események
                  </Button>
                </Link>

                {/* Jutalmak Category */}
                <div className="pt-2 pb-1">
                  <span className="px-3 text-xs font-semibold text-orange-500 uppercase tracking-wider">🎁 Jutalmak</span>
                </div>
                <Link to="/wheel" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Gift className="mr-2 h-4 w-4" /> Szerencsekerék
                  </Button>
                </Link>
                <Link to="/slots" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Ticket className="mr-2 h-4 w-4" /> Nyerőgép
                  </Button>
                </Link>
                <Link to="/blackjack" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Spade className="mr-2 h-4 w-4" /> Blackjack
                  </Button>
                </Link>
                <Link to="/lottery" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Trophy className="mr-2 h-4 w-4" /> Sorsolás
                  </Button>
                </Link>
                <Link to="/shop" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <ShoppingBag className="mr-2 h-4 w-4" /> Pontbolt
                  </Button>
                </Link>
                <Link to="/giftcards" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Gift className="mr-2 h-4 w-4" /> Ajándékkártyák
                  </Button>
                </Link>

                {/* Szolgáltatások Category */}
                <div className="pt-2 pb-1">
                  <span className="px-3 text-xs font-semibold text-orange-500 uppercase tracking-wider">📋 Szolgáltatások</span>
                </div>
                <Link to="/calendar" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Calendar className="mr-2 h-4 w-4" /> Naptár
                  </Button>
                </Link>
                <Link to="/reminders" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <Bell className="mr-2 h-4 w-4" /> Emlékeztetők
                  </Button>
                </Link>
                <Link to="/damage-reports" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10 pl-6">
                    <AlertTriangle className="mr-2 h-4 w-4" /> Kárbejelentés
                  </Button>
                </Link>

                {/* Saját */}
                <div className="pt-2 pb-1">
                  <span className="px-3 text-xs font-semibold text-orange-500 uppercase tracking-wider">👤 Saját</span>
                </div>
                <Link to="/rentals" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 pl-6">
                    <Clock className="mr-2 h-4 w-4" /> Bérléseim
                  </Button>
                </Link>
              </>
            )}

            {isAdmin && (
              <>
                <div className="pt-2 pb-1">
                  <span className="px-3 text-xs font-semibold text-blue-500 uppercase tracking-wider">⚙️ Admin</span>
                </div>
                <Link to="/admin" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 pl-6">
                    <Shield className="mr-2 h-4 w-4" /> Admin Panel
                  </Button>
                </Link>
                <Link to="/admin/chat" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 pl-6">
                    <MessageCircle className="mr-2 h-4 w-4" /> Support Chat
                  </Button>
                </Link>
                <Link to="/employees" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 pl-6">
                    <Users className="mr-2 h-4 w-4" /> Alkalmazottak
                  </Button>
                </Link>
              </>
            )}

            {isLandlord && (
              <>
                <div className="pt-2 pb-1">
                  <span className="px-3 text-xs font-semibold text-blue-500 uppercase tracking-wider">🏠 Bérbeadó</span>
                </div>
                <Link to="/landlord" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 pl-6">
                    <Key className="mr-2 h-4 w-4" /> Bérbeadói Panel
                  </Button>
                </Link>
              </>
            )}

            {((user?.role || '').toLowerCase() === 'promoter' || isAdmin) && (
              <>
                <div className="pt-2 pb-1">
                  <span className="px-3 text-xs font-semibold text-pink-500 uppercase tracking-wider">📢 Promóter</span>
                </div>
                <Link to="/promoter" onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-pink-400 hover:text-pink-300 hover:bg-pink-900/20 pl-6">
                    <Megaphone className="mr-2 h-4 w-4" /> Promóter Panel
                  </Button>
                </Link>
              </>
            )}

            <div className="pt-3 border-t border-white/10 mt-3">
              <a href={systemSettings?.discord_invite_url || "https://discord.gg/S2CH8JSAP3"} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)}>
                <Button variant="outline" className="w-full justify-start border-blue-500/50 text-blue-400">Discord</Button>
              </a>
            </div>
            
            {!isAuthenticated && (
              <Button onClick={login} className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white mt-4">Bejelentkezés</Button>
            )}
            {isAuthenticated && (
              <Button onClick={logout} variant="destructive" className="w-full mt-4 bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50">Kijelentkezés</Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
